import {
  ICreateInternalOrganisationRequest,
  IContactResponse,
  IInvoiceHistoryMetricsResponse,
  IPaymentForecastResponse,
  PaymentForecastBasis,
  IInvoiceListResponse,
  IOrganisationEmailMetrics,
  IOrganisationMarketingData,
  IOrganisationResponse,
  IOrganisationResponseItem,
  OnboardingExtraFeature,
  PolicyType,
} from 'shared';

import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { CacheTags } from '../../types/cacheTags';
import { emptySplitApi } from '../baseApi';

export const organisationApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchOrganisations: builder.query<IOrganisationResponseItem[], string>({
      query: () => ({
        url: `organisations`,
        method: `GET`,
      }),
      transformResponse: (rawResult: IOrganisationResponse) => {
        return rawResult?.organisations;
      },
      providesTags: result => result ?
        [
          ...result.map(({ id }) => ({ type: CacheTags.Organisations, id })),
          { type: CacheTags.Organisations, id: `LIST` },
        ]
        : [{ type: CacheTags.Organisations, id: `LIST` }],
    }),
    fetchInvoiceHistoryMetrics: builder.query<IInvoiceHistoryMetricsResponse, { orgId: string; period?: number }>({
      query: ({ orgId, period }) => ({
        url: `metrics/history`,
        method: `GET`,
        params: {
          previousMonths: period,
        },
        headers: {
          [orgHeader]: orgId,
        },
      }),
    }),
    fetchPaymentForecastMetrics: builder.query<IPaymentForecastResponse, { orgId: string; days: number; basis: PaymentForecastBasis }>({
      query: ({ orgId, days, basis }) => ({
        url: `metrics/forecast`,
        method: `GET`,
        headers: {
          [orgHeader]: orgId,
        },
        params: { days, basis },
      }),
    }),
    fetchEmailMetrics: builder.query<IOrganisationEmailMetrics, { orgId: string }>({
      query: ({ orgId }) => ({
        url: `metrics/email`,
        method: `GET`,
        headers: {
          [orgHeader]: orgId,
        },
      }),
    }),
    fetchSlowPayersMetrics: builder.query<IContactResponse, { orgId: string}>({
      query: ({ orgId }) => ({
        url: `contact`,
        method: `GET`,
        params: {
          sortColumn: `oldestDaysOverdue`,
          sortDirection: `desc`,
          // Only contacts actually overdue (cents)
          overdueBalance: 1,
          overdueBalanceOperator: `>=`,
          limit: 10,
          page: 1,
        },
        headers: {
          [orgHeader]: orgId,
        },
      }),
      providesTags: [{ type: CacheTags.BadContacts, id: `LIST` }],
    }),
    fetchBadInvoicesMetrics: builder.query<IInvoiceListResponse, { orgId: string}>({
      query: ({ orgId }) => ({
        // We can just get the invoices from invoices endpoint for now, with defined params
        url: `invoice`,
        method: `GET`,
        headers: {
          [orgHeader]: orgId,
        },
        params: {
          limit: 10,
          page: 1,
          sortColumn: `due_date_string`,
          sortType: `asc`,
          xeroStatus: `AUTHORISED`,
          daysOverdue: 0,
          daysOverdueOperator: `>`,
        },
      }),
      providesTags: [{ type: CacheTags.BadInvoices, id: `LIST` }],
    }),
    createInternalOrganisation: builder.mutation<{ id: string }, ICreateInternalOrganisationRequest>({
      query: data => ({
        url: `organisations/internal`,
        method: `POST`,
        data,
      }),
      invalidatesTags: [{ type: CacheTags.Organisations, id: `LIST` }],
    }),
    inviteSupport: builder.mutation<void, string>({
      query: orgId => ({
        url: `organisation/invite-support`,
        method: `POST`,
        headers: {
          [orgHeader]: orgId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    removeSupport: builder.mutation<void, string>({
      query: orgId => ({
        url: `organisation/remove-support`,
        method: `POST`,
        headers: {
          [orgHeader]: orgId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    enablePinchPayments: builder.mutation<void, string>({
      query: orgId => ({
        url: `organisation/payments/pinch`,
        method: `PUT`,
        headers: {
          [orgHeader]: orgId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    disablePinchPayments: builder.mutation<void, string>({
      query: orgId => ({
        url: `organisation/payments/pinch`,
        method: `DELETE`,
        headers: {
          [orgHeader]: orgId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    enableCustomPayments: builder.mutation<void, {
      organisationId: string;
      paymentLinkTemplate: string;
    }>({
      query: ({
        organisationId,
        paymentLinkTemplate,
      }) => ({
        url: `organisation/payments/custom`,
        method: `PUT`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          paymentLinkTemplate,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    disableCustomPayments: builder.mutation<void, string>({
      query: organisationId => ({
        url: `organisation/payments/custom`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    enableRPSPayments: builder.mutation<void, {
      organisationId: string;
      paymentLinkTemplate: string;
    }>({
      query: ({
        organisationId,
        paymentLinkTemplate,
      }) => ({
        url: `organisation/payments/rps`,
        method: `PUT`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          paymentLinkTemplate,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    disableRPSPayments: builder.mutation<void, string>({
      query: organisationId => ({
        url: `organisation/payments/rps`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    saveWelcomeQuestions: builder.mutation<void, IOrganisationMarketingData & { organisationId: string }>({
      query: data => ({
        url: `organisation/marketing-data`,
        method: `PATCH`,
        data,
        headers: {
          [orgHeader]: data.organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    saveOnboardingGroupPolicies: builder.mutation<void, { organisationId: string; selectedPolicies: (PolicyType | OnboardingExtraFeature)[] }>({
      query: data => ({
        url: `organisation/onboarding/group-policies`,
        method: `POST`,
        data,
        headers: {
          [orgHeader]: data.organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Organisations },
        { type: CacheTags.Policies, id: `LIST` },
        { type: CacheTags.Groups, id: `LIST` },
      ],
    }),
    saveOnboardingComplete: builder.mutation<void, string>({
      query: organisationId => ({
        url: `organisation/onboarding/complete`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    saveOnboardingSkipped: builder.mutation<void, string>({
      query: organisationId => ({
        url: `organisation/onboarding/complete`,
        method: `POST`,
        data: {
          skipped: true,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    dismissWelcomeMessage: builder.mutation<void, string>({
      query: organisationId => ({
        url: `organisation/dismiss-welcome-message`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }],
    }),
    leaveOrganisation: builder.mutation<void, { organisationId: string }>({
      query: ({ organisationId }) => ({
        url: `organisation/leave`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.Organisations }, { type: CacheTags.User }],
    }),
  }),
});

export const {
  useFetchOrganisationsQuery,
  useCreateInternalOrganisationMutation,
  useInviteSupportMutation,
  useRemoveSupportMutation,
  useEnablePinchPaymentsMutation,
  useDisablePinchPaymentsMutation,
  useEnableCustomPaymentsMutation,
  useDisableCustomPaymentsMutation,
  useEnableRPSPaymentsMutation,
  useDisableRPSPaymentsMutation,
  useSaveWelcomeQuestionsMutation,
  useSaveOnboardingGroupPoliciesMutation,
  useSaveOnboardingCompleteMutation,
  useSaveOnboardingSkippedMutation,
  useFetchInvoiceHistoryMetricsQuery,
  useFetchEmailMetricsQuery,
  useFetchPaymentForecastMetricsQuery,
  useFetchSlowPayersMetricsQuery,
  useFetchBadInvoicesMetricsQuery,
  useDismissWelcomeMessageMutation,
  useLeaveOrganisationMutation,
} = organisationApi;
